import React, { useEffect, useState } from 'react';
import {
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material';
import { makeApiCall } from '../../../config/api';

const ManagerSelect = ({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  error = false,
  helperText = '',
  language = 'en'
}) => {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const labels = language === 'es' ? {
    loading: 'Cargando gerentes...',
    empty: 'No hay gerentes activos configurados. Pida a la oficina que configure gerentes en Administración de Usuarios.'
  } : {
    loading: 'Loading managers...',
    empty: 'No active managers are configured. Ask the office to configure managers in User Management.'
  };

  useEffect(() => {
    let isMounted = true;

    const loadManagers = async () => {
      try {
        setLoading(true);
        const response = await makeApiCall('/api/employee-forms/managers');
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to load managers');
        }

        if (isMounted) {
          setManagers(data.managers || []);
          setLoadError('');
        }
      } catch (err) {
        console.error('Error loading managers:', err);
        if (isMounted) {
          setManagers([]);
          setLoadError(err.message || 'Failed to load managers');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadManagers();
    return () => {
      isMounted = false;
    };
  }, []);

  if (loading) {
    return (
      <FormControl fullWidth disabled>
        <InputLabel>{label}</InputLabel>
        <Select value="" label={label} inputProps={{ 'aria-label': label }}>
          <MenuItem value="">
            <CircularProgress size={16} sx={{ mr: 1 }} />
            {labels.loading}
          </MenuItem>
        </Select>
      </FormControl>
    );
  }

  // TKT-261: previously this empty state rendered a full-width Alert
  // banner that visually overlapped the form field next to it (Naomi's
  // Uniform Form screenshot showed the warning crossing out the
  // Employee Name input). Render a disabled dropdown with the same
  // text as helper text so the form layout stays intact and the user
  // can still complete the rest of the fields. The dropdown still
  // visually communicates "no managers available" without blocking
  // anything else.
  if (loadError || managers.length === 0) {
    return (
      <FormControl fullWidth required={required} disabled error={!!loadError}>
        <InputLabel>{label}</InputLabel>
        <Select value="" label={label} inputProps={{ 'aria-label': label }}>
          <MenuItem value="">
            <em>{loadError ? loadError : labels.empty}</em>
          </MenuItem>
        </Select>
        <FormHelperText>{loadError || labels.empty}</FormHelperText>
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth required={required} disabled={disabled} error={error}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        label={label}
        onChange={(event) => {
          onChange(event.target.value || '');
        }}
        inputProps={{ 'aria-label': label }}
      >
        {managers.map((manager) => (
          <MenuItem key={manager.id} value={manager.name}>
            {manager.name}
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default ManagerSelect;
